import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAuth } from 'context/AuthContext';
import { useMenuData } from 'context/MenuDataContext';

import { genConfig } from 'funifier-nice-avatar';

import { ComportamentoHelpModal } from 'components/comportamento-help-modal';
import { SeloMoedasModal } from 'components/moeda-help-modal';
import Button from 'components/ui/button';
import { useModal } from 'context/ModalContext';
import { NotAllowed } from 'pages/NotAllowed';
import { useMediaQuery } from 'react-responsive';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import CycleSelector from 'components/cycle-selector';
import { checkTeamsRecognition, getSelos } from 'services/dashboard.service';
import { TaskCycles } from './components/task-cycles';
import { TaskCyclesClosed } from './components/task-cycles-closed';
import { shouldShowPrefixAchievementsLink, shouldShowTaskCyclesSection } from './taskCyclesVisibility';
import './styles.scss';

import { GroupDetailModal } from 'components/group-detail-modal';
import NewFeatures from 'components/new-features';
import Notification from 'components/notification';
import { Company } from './components/company';
import { NotificationsRow } from './components/notifications-row';
import { PrefixAchievements } from './components/prefix-achievements';
import { ProfileCard } from './components/profile-card';
import { RecognizePanel } from './components/recognize-panel';
import Quiz from 'components/quiz';
import { TaskCyclesEcoa } from './components/task-cycles-ecoa';
import { ServerClockProvider } from './server-clock';
import { GroupDetailModalExtrato } from 'components/group-detail-modal-extrato';
import { canAccessExperienceCatalog } from 'utils/experience-catalog';
import { readOncePerSession, SESSION_KEYS, setSessionValue } from 'utils/session-reads';

import PartialTask from 'components/partial-task';
import PartialRecognition from 'components/partial-recognition';
import CatalogRestockModal from './components/catalog-restock-modal';
import { NewYearEnvelopeModal } from 'components/new-year-envelope-modal';
import TeamsEvaluationModal from 'components/modal-avaliacao-teams';

import { homeReadsPlan } from './home-reads';
import { useBackNavigationRefresh } from './useBackNavigationRefresh';
import { useCulturePoints } from './useCulturePoints';
import { usePartialDrafts } from './usePartialDrafts';
import { useProfileSummary } from './useProfileSummary';

const defautConfig = genConfig();

export function Dashboard() {
  const { user, userInfo, refreshUser, currentCycle, realCycle, cycleSelected, loadingInfo, userRevision } = useAuth();
  const { tasksManager, tasksDuplaManager } = useMenuData();
  const [showQuiz, setShowQuiz] = useState(false);

  const [hasTeamsRecognition, setHasTeamsRecognition] = useState<boolean>();

  // Sinais da fila de modais (./modal-queue.ts): a notificacao do catalogo so abre
  // depois que toda automatica conhecida apareceu ou decidiu nao aparecer (R10).
  const [partialTaskClosed, setPartialTaskClosed] = useState(false);
  const [partialRecognitionClosed, setPartialRecognitionClosed] = useState(false);
  // Fixo na visita: a TeamsEvaluationModal decidiu nao aparecer (pesquisa ja respondida ou
  // falha). Separado de hasTeamsRecognition, que so muda quando a modal e fechada.
  const [teamsRecognitionSkipped, setTeamsRecognitionSkipped] = useState(false);

  const [closeFeatures, setCloseFeatures] = useState(false);

  const [sealList, setSealList] = useState<any[]>();
  const [loadingCulturaList, setLoadingCulturaList] = useState(true);

  const location = useLocation();
  const navigate = useNavigate();

  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 1023px)' });
  const { showModal, openModal } = useModal();

  const userId = user?._id;
  const cycleId = cycleSelected?._id;

  const waitingBackRefresh = useBackNavigationRefresh(refreshUser);

  const pontosCultura = useCulturePoints(userId, cycleId, userRevision, waitingBackRefresh);
  const profile = useProfileSummary(userInfo?.chave, cycleId, userInfo?.grupo);

  const avatarConfig = user?.extra?.avatar || defautConfig;

  const groupInfo = useMemo<any>(
    () => (userInfo?.grupo ? { ro: userInfo.ro, grupo_definido: userInfo.grupo_definido, ...userInfo.grupo } : undefined),
    [userInfo],
  );

  const handleAvatarClick = useCallback(() => {
    showModal('avatar', { config: avatarConfig });
  }, [avatarConfig, showModal]);

  // Selos só leem coleções de fechamento, congeladas: 1 vez por sessão (DCE-29).
  useEffect(() => {
    if (!userId || !cycleId) return;

    let active = true;

    setLoadingCulturaList(true);
    readOncePerSession(SESSION_KEYS.selos(userId), getSelos)
      .then(list => {
        if (!active) return;
        setSealList([...list]);
        setLoadingCulturaList(false);
      })
      .catch(error => console.error('Erro ao carregar os selos:', error));

    return () => {
      active = false;
    };
  }, [userId, cycleId]);

  useEffect(() => {
    if (!userId || !cycleId) return;

    const returnURL = sessionStorage.getItem('return');

    if (returnURL) {
      sessionStorage.removeItem('return');
      navigate(returnURL);
    }
  }, [userId, cycleId, navigate]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const updateAvatar = searchParams.get('avatar');
    const openQuiz = searchParams.get('quiz');
    const openTutorial = searchParams.get('tutorial');

    if (updateAvatar) {
      setTimeout(() => {
        handleAvatarClick();
      }, 1e2);

      searchParams.delete('avatar');
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }

    if (openQuiz) {
      setShowQuiz(true);

      searchParams.delete('quiz');
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }

    if (openTutorial) {
      searchParams.delete('tutorial');
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const cycleIsOpen = useMemo(
    () => realCycle?.find(e => e._id === cycleSelected?._id)?.status === 'open',
    [realCycle, cycleSelected],
  );

  const cycleIsClosed = useMemo(
    () => realCycle?.find(e => e._id === cycleSelected?._id)?.status === 'closed',
    [realCycle, cycleSelected],
  );

  const { canRegister, canRecognize, showEcoa, showEngagement } = homeReadsPlan({
    user,
    userInfo,
    cycleIsOpen: !!cycleIsOpen && !!currentCycle,
  });
  const { hasPartialTask, hasPartialRecognition } = usePartialDrafts({ canRegister, canRecognize });

  const hasUserInfo = !!userInfo;
  const hasCurrentCycle = !!currentCycle;

  // A pesquisa do Teams é decidida 1 vez por sessão; fechada, fica fechada até F5/login (DCE-38).
  useEffect(() => {
    if (!userId || !hasUserInfo || !hasCurrentCycle) return;

    let active = true;

    readOncePerSession(SESSION_KEYS.teamsRecognition(userId), checkTeamsRecognition)
      .then(hasTeams => {
        if (active) setHasTeamsRecognition(hasTeams);
      })
      .catch(error => console.error('Erro ao verificar reconhecimento no Teams:', error));

    return () => {
      active = false;
    };
  }, [userId, hasUserInfo, hasCurrentCycle]);

  function closeTeamsRecognition() {
    if (userId) setSessionValue(SESSION_KEYS.teamsRecognition(userId), false);
    setHasTeamsRecognition(false);
  }

  if (!user) return null;

  if (!user.extra.id) {
    return <NotAllowed />;
  }

  const taskCyclesEligibility = {
    loadingInfo,
    isRede: !!user.extra.rede,
    userInfo,
    tasksManager,
    tasksDuplaManager,
  };

  // Governa a seção TaskCycles da home. Some com o ciclo fechado, porque nesse caso a home
  // troca a seção por `TaskCyclesClosed`.
  const showTaskCycles = shouldShowTaskCyclesSection({ ...taskCyclesEligibility, cycleIsClosed });

  // Governa o item de menu "Realizações do seu prefixo". Mesma elegibilidade, mas sobrevive
  // ao ciclo fechado: a listagem é histórica e tem seletor de ciclo próprio.
  const showPrefixAchievementsLink = shouldShowPrefixAchievementsLink(taskCyclesEligibility);

  return (
    <>
      <Outlet />

      {/* D3: max-w não tem escala de spacing no Tailwind 3.3. */}
      <div className="mx-auto flex w-full max-w-[1000px] flex-col gap-8 px-4 pb-20 pt-4 sm:px-6 lg:gap-10 lg:p-0">
        {isTabletOrMobile && <CycleSelector />}

        <ProfileCard
          user={user}
          userInfo={userInfo}
          profile={profile}
          groupInfo={groupInfo}
          seal={!loadingCulturaList && sealList?.length ? sealList[0] : undefined}
          points={{ jogaJunto: pontosCultura.joga_junto, realizacoes: pontosCultura.tasks }}
          onAvatarClick={handleAvatarClick}
        />

        {user.extra.rede && (
          <section className="flex flex-col gap-6 rounded-jj-xl bg-brand-white p-6 text-content shadow-jj-02 sm:flex-row sm:items-center">
            <button type="button" onClick={() => navigate('/?tutorial=open')} className="w-full sm:w-[45%]">
              <img
                src="https://s3.amazonaws.com/funifier/games/635ae06f8cf58e411eeb0b8e/images/66229d868b31775619ea0284_original_imagem.png"
                alt="Tutorial joga junto rede"
                className="w-full"
              />
            </button>
            <div className="flex flex-1 flex-col items-center gap-6 text-center">
              <p>
                Os reconhecimentos enviados para colegas que fazem a diferença na sua rotina de trabalho podem mudar o dia de
                cada um deles.
              </p>
              <Button variant="solido" onClick={() => navigate('/?tutorial=open')} aria-label="Assistir video de tutorial.">
                Assistir vídeo
              </Button>
            </div>
          </section>
        )}

        {/* Aviso não lido mais recente; some sozinha sem aviso (HOME-40 a HOME-42). */}
        <NotificationsRow waitingRefresh={waitingBackRefresh} />

        <RecognizePanel
          balance={user?.point_categories?.point_behavior || 0}
          cycleIsOpen={!!cycleIsOpen && !!currentCycle}
          cycleIsClosed={!!cycleIsClosed}
          cycleFinish={currentCycle?.date_finish}
          recognitionEnabled={user?.extra?.recognition !== false}
          readOnly={!!userInfo?.visualizador}
          hasPartialRecognition={!!hasPartialRecognition}
          prefixo={userInfo?.prefixo}
          // Esperando só enquanto os dados do ciclo carregam: num ciclo antigo em que o funci não participava, eles
          // não vêm, e a espera ficava para sempre (CAN-40).
          prefixoLoading={loadingInfo || (!userInfo && !cycleSelected)}
        />

        {showPrefixAchievementsLink && <PrefixAchievements prefixo={userInfo?.prefixo} cycleId={cycleSelected?._id} />}

        {/* Um só relógio da hora do servidor para as duas seções que dependem dela (DCE-35, DCE-36). */}
        <ServerClockProvider enabled={showTaskCycles || showEcoa}>
          {showTaskCycles && (
            <TaskCycles
              taskManager={[
                ...(Array.isArray(tasksManager) ? tasksManager : []),
                ...(Array.isArray(tasksDuplaManager) ? tasksDuplaManager : []),
              ]}
              hasPartialTask={hasPartialTask}
            />
          )}

          {cycleIsClosed && <TaskCyclesClosed />}

          {showEcoa && <TaskCyclesEcoa cycleIsClosed={!!cycleIsClosed} />}
        </ServerClockProvider>

        {/* A moldura mudou de lugar para dentro de `Company` (HOM-01.5): só ele sabe se algum
            card sobrou no ciclo, e sem card a caixa inteira precisa sumir. */}
        {showEngagement && <Company />}
      </div>
      <NewFeatures player={userInfo} send={() => setCloseFeatures(true)} />
      <ComportamentoHelpModal />
      <SeloMoedasModal />
      <GroupDetailModal group={groupInfo} />
      <GroupDetailModalExtrato cycleLabel={cycleSelected?.title ? cycleSelected?.title : ''} />

      {location.pathname === '/' && user?.extra?.actions !== false && <Notification />}

      {showQuiz && <Quiz onClose={() => setShowQuiz(false)} setQuizAnswered={() => {}} />}

      <NewYearEnvelopeModal />

      {closeFeatures && hasPartialTask && <PartialTask onClose={() => setPartialTaskClosed(true)} />}

      {closeFeatures && hasPartialRecognition && !hasPartialTask && (
        <PartialRecognition onClose={() => setPartialRecognitionClosed(true)} />
      )}

      {closeFeatures && hasTeamsRecognition && !teamsRecognitionSkipped && (
        <TeamsEvaluationModal onClose={closeTeamsRecognition} onSkip={() => setTeamsRecognitionSkipped(true)} />
      )}

      {/* A ULTIMA modal automatica (R10): so abre com o Dashboard quieto — ver ./modal-queue.ts. */}
      <CatalogRestockModal
        enabled={canAccessExperienceCatalog(userInfo)}
        playerId={userInfo?.chave}
        signals={{
          acceptSettled: closeFeatures,
          partialTaskSettled: hasPartialTask === false || partialTaskClosed,
          partialRecognitionSettled:
            hasPartialRecognition === false || hasPartialTask === true || partialRecognitionClosed,
          teamsRecognitionSettled: hasTeamsRecognition === false || teamsRecognitionSkipped,
          quizClosed: !showQuiz,
          contextSlotFree: openModal === undefined,
        }}
      />
    </>
  );
}
